import { Injectable } from '@angular/core';
import {
    Chat,
    ChatItem,
    ChatItemMessage,
    EphemeralChatItemMessage,
    Prompt,
    Thread,
} from '../models/ai-chat-models/chat.model';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';
import { AiChatBaseService } from './ai-chat-base.service';
import { HttpClient } from '@angular/common/http';
import { AppStateService } from './app-state-service';
import { UniversalSnackbarService } from './universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { AI_ASSISTANT_ROLE, USER_ROLE } from '../constants/settingContants';
@Injectable({
    providedIn: 'root',
})
export class AiChatService extends AiChatBaseService {
    path = '/AiChat';

    private chat_bs$: BehaviorSubject<Chat> = new BehaviorSubject<Chat>(null);
    private threads_bs$: BehaviorSubject<Thread[]> = new BehaviorSubject<
        Thread[]
    >([]);
    private chatLoading_bs$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);
    private aiBuffering_bs$: BehaviorSubject<boolean> = new BehaviorSubject(
        false,
    );
    private threadsBuffering_bs$: BehaviorSubject<boolean> =
        new BehaviorSubject(false);
    private newThreadBeingSaved_bs$: BehaviorSubject<boolean> =
        new BehaviorSubject(false);

    constructor(
        protected httpClient: HttpClient,
        private appStateService: AppStateService,
        private snackbar: UniversalSnackbarService,
    ) {
        super(httpClient);
    }

    public get chat(): Observable<Chat> {
        return this.chat_bs$.asObservable();
    }

    public get threads(): Observable<Thread[]> {
        return this.threads_bs$.asObservable();
    }

    public get chatLoading(): Observable<boolean> {
        return this.chatLoading_bs$.asObservable();
    }

    public get aiBuffering(): Observable<boolean> {
        return this.aiBuffering_bs$.asObservable();
    }

    public get threadBuffering(): Observable<boolean> {
        return this.threadsBuffering_bs$.asObservable();
    }

    public get newThreadSaving(): Observable<boolean> {
        return this.newThreadBeingSaved_bs$.asObservable();
    }

    public getUserThreads(): void {
        this.threadsBuffering_bs$.next(true);
        this.httpClient
            .get<
                Thread[]
            >(`${this.baseUrl}${this.path}/threads/${this.appStateService.me$.value.id}`)
            .subscribe({
                next: (threads: Thread[]) => {
                    const chat: Chat = this.chat_bs$.value;

                    // If a new thread is saved while the inital list is loading, add it.
                    // chat contains a user response, ephermeral or not
                    // threads doesn't contain the current loaded thread
                    // chat name can be blank, but null means the thread hasn't touched the backend yet
                    if (
                        chat.completeChat.some(
                            (c: ChatItem) => c.message.role === USER_ROLE,
                        ) &&
                        !threads.some(
                            (t: Thread) => t.threadId === chat[0]?.threadId,
                        ) &&
                        chat?.threadName !== null
                    ) {
                        this.threads_bs$.next([
                            ...threads,
                            new Thread({
                                threadId: chat.threadId,
                                userId: this.appStateService.me$.value.id,
                                createDate:
                                    chat.completeChat[0].message.createdAt,
                                threadName: chat.threadName,
                            }),
                        ]);

                        if (this.newThreadBeingSaved_bs$.value) {
                            this.newThreadBeingSaved_bs$.next(false);
                        }
                        this.threadsBuffering_bs$.next(false);
                    } else {
                        this.threads_bs$.next(threads);
                        if (this.newThreadBeingSaved_bs$.value) {
                            this.newThreadBeingSaved_bs$.next(false);
                        }

                        this.threadsBuffering_bs$.next(false);
                    }
                },
                error: () => {
                    this.threadsBuffering_bs$.next(false);
                    this.snackbar.open(MSG.AI_CHAT_THREADS_F);
                },
            });
    }

    public getChatByThreadId(threadId: string): void {
        this.chatLoading_bs$.next(true);
        this.manageThreads();
        this.chat_bs$.next(null);
        this.httpClient
            .get<Chat>(
                `${this.baseUrl}${this.path}/thread/${this.appStateService.me$.value.id}/${threadId}`,
            )
            .subscribe({
                next: (chat: Chat) => {
                    this.chatLoading_bs$.next(false);
                    this.chat_bs$.next(chat);
                },
                error: () => {
                    this.chatLoading_bs$.next(false);
                    this.snackbar.open(MSG.AI_CHAT_THREAD_LOAD_F);
                },
            });
    }

    public deleteThreadsById(threadIds: string[]): void {
        const currentLoadedThreads: number = this.threads_bs$.value.length;
        this.httpClient
            .delete<
                Thread[]
            >(`${this.baseUrl}${this.path}/thread/${this.appStateService.me$.value.id}`, { body: [...threadIds] })
            .subscribe({
                next: (returnedThreads: Thread[]) => {
                    if (returnedThreads.length != currentLoadedThreads) {
                        // Update threads if a visible thread is removed
                        this.threads_bs$.next(returnedThreads);
                        if (
                            threadIds.some(
                                (threadId: string) =>
                                    threadId === this.chat_bs$.value.threadId,
                            )
                        ) {
                            // If the user deleted the thread/chat they are in
                            this.chat_bs$.next(null);
                        }
                    } else {
                        // Don't update if it deleted a thread void of user response that is not visible
                        return;
                    }
                },
                error: () => {
                    this.chat_bs$.next(this.chat_bs$.value); // Emit to complete ChatComponenet subscription on error
                    this.snackbar.open(MSG.AI_CHAT_DELETE_F);
                },
                complete: () => {
                    if (!(this.chat_bs$.value === null)) {
                        this.chat_bs$.next(this.chat_bs$.value); // Emit to complete ChatComponenet subscription on non-error
                    }
                },
            });
    }

    public sendWelcomeMessage(): void {
        if (this.threads_bs$.value.length === 0) {
            // Threads may exist before message is sent if user creates a new chat after threads have loaded
            this.getUserThreads();
        }

        this.chatLoading_bs$.next(true);
        this.chat_bs$.next(null);
        this.httpClient
            .post<Chat>(
                this.baseUrl + this.path,
                new Prompt(
                    '{Display Welcome Message}',
                    null,
                    this.appStateService.me$.value.id,
                    [
                        /* attachments */
                    ],
                ),
            )
            .pipe(tap((chat: Chat) => (chat.threadName = null))) // a newly created thread from a thread without a name.
            .subscribe({
                next: (chat: Chat) => {
                    this.chatLoading_bs$.next(false);
                    this.chat_bs$.next(chat);
                },
                error: () => {
                    this.chatLoading_bs$.next(false);
                    this.snackbar.open(MSG.AI_CHAT_MESSAGE_F);
                },
            });
    }

    public sendMessage(message: string): void {
        const currentChat: Chat = this.chat_bs$.value;
        const temporaryMessage: ChatItemMessage = new EphemeralChatItemMessage({
            id: 'temp-' + Math.random().toString(36).substring(2, 15),
            role: USER_ROLE,
            contentItems: [{ type: 'user', text: message }],
            createdAt: new Date().toISOString(),
            attachments: [],
        });

        const prompt: Prompt = new Prompt(
            message,
            currentChat.threadId,
            this.appStateService.me$.value.id,
            [],
        );

        currentChat.completeChat.push({
            threadId: currentChat.threadId,
            message: temporaryMessage,
        });

        if (
            this.chat_bs$.value.completeChat.filter(
                (chatItem: ChatItem) =>
                    chatItem.message.role == AI_ASSISTANT_ROLE,
            ).length === 1
        ) {
            // if there is only one ai message and you are sending your follow up
            this.newThreadBeingSaved_bs$.next(true);
        }

        this.chat_bs$.next(currentChat);

        this.aiBuffering_bs$.next(true);

        this.httpClient
            .post<Chat>(this.baseUrl + this.path, prompt)
            .pipe(tap((chat: Chat) => chat.threadName ?? '')) // a thread without a name from a newly created thread
            .subscribe({
                next: (chat: Chat) => {
                    this.chat_bs$.next(chat);

                    const threads = this.threads_bs$.value;
                    const exists = threads.some(
                        (thread) => thread.threadId === chat.threadId,
                    );

                    if (!exists) {
                        this.threads_bs$.next([
                            ...threads,
                            new Thread({
                                threadId: chat.threadId,
                                userId: this.appStateService.me$.value.id,
                                createDate:
                                    chat.completeChat[0].message.createdAt,
                                threadName: chat.threadName,
                            }),
                        ]);
                    } else {
                        this.threads_bs$.next(
                            threads.map((thread) =>
                                thread.threadId === chat.threadId
                                    ? {
                                          ...thread,
                                          threadName:
                                              chat.threadName ??
                                              thread.threadName,
                                      }
                                    : thread,
                            ),
                        );
                    }

                    if (this.newThreadBeingSaved_bs$.value) {
                        this.newThreadBeingSaved_bs$.next(false);
                    }
                    this.aiBuffering_bs$.next(false);
                },
                error: () => {
                    this.aiBuffering_bs$.next(false);
                    this.snackbar.open(MSG.AI_CHAT_MESSAGE_F);
                },
            });
    }

    public manageThreads(): void {
        if (!this.chat_bs$.value) {
            return;
        }
        const chat: Chat = this.chat_bs$.value;
        if (
            !chat.completeChat.some(
                (chatItem: ChatItem) => chatItem.message.role === USER_ROLE,
            )
        ) {
            this.chat_bs$.next(null);
            this.deleteThreadsById([chat.threadId]);
        }
    }
}
